import { DOCUMENT_MAX_SIZE } from '@lucidtech/las-sdk-browser';
import { memo, useCallback, useMemo, useState } from 'react';

import {
  Button,
  Dialog,
  DropZone,
  DropZoneButton,
  Heading,
  ListBox,
  ListBoxItem,
  Modal,
  ModalOverlay,
  ModalOverlayProps,
  Popover,
  Select,
  SelectValue,
  Text,
} from '@/components/core';
import { ArrowIcon, ChevronIcon, CloseIcon, InfoIcon, SaveIcon } from '@/components/icons';
import { MappingItem } from '@/store';
import { generateId, OmitChildren } from '@/utils';

type UploadProgressProps = {
  uploadProgress: number;
};

const IUploadProgress = ({ uploadProgress }: UploadProgressProps) => {
  return (
    <>
      <Text className="flex gap-2">
        <SaveIcon className="size-4 animate-spin stroke-gray-500" />
        Uploading CSV...
      </Text>
      <Text size="xs">({uploadProgress} completed)</Text>
    </>
  );
};

const UploadProgress = memo(IUploadProgress) as typeof IUploadProgress;

export type ImportFromCSVDialogProps = OmitChildren<ModalOverlayProps> & {
  onImport: (mapping: MappingItem[]) => void;
  onClose?: () => void;
};

const IImportFromCSVDialog = ({ onImport: onImportFn, onClose, ...rest }: ImportFromCSVDialogProps) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | undefined>();
  const [isUploading, setIsUploading] = useState(false);
  const [rows, setRows] = useState<string[][] | undefined>();
  const [columns, setColumns] = useState<string[] | undefined>();
  const [fromColumn, setFromColumn] = useState<string | undefined>();
  const [toColumn, setToColumn] = useState<string | undefined>();

  const onDropFiles = useCallback(async (files: File[]) => {
    setUploadProgress(0);
    setUploadError(undefined);
    setIsUploading(true);
    setRows(undefined);
    setColumns(undefined);

    try {
      if (files.length !== 1) throw new Error('Expected one file to be uploaded.');

      const file = files[0];
      const text = await file.text();
      const csvData = text
        .trim()
        .split('\n')
        .map((line) => line.split(',').map((value) => value.replace(/^"(.*)"$/, '$1').trim()));

      if (csvData.length < 2) throw new Error('CSV must have at least one data row.');

      setUploadProgress((x) => x + 1);
      setRows(csvData.slice(1));
      setColumns(csvData[0]);
    } catch (error) {
      if (error instanceof Error) {
        setUploadError(error.message);
      }
    } finally {
      setIsUploading(false);
    }
  }, []);

  const isImportReady = useMemo(
    () => fromColumn != null && toColumn != null && rows != null && columns != null,
    [columns, fromColumn, rows, toColumn]
  );

  const onImport = useCallback(() => {
    if (!isImportReady) return;

    const mapping: MappingItem[] = [];
    const fromIndex = columns!.indexOf(fromColumn!);
    const toIndex = columns!.indexOf(toColumn!);

    for (const row of rows!) {
      mapping.push({
        id: generateId(),
        from: row[fromIndex],
        to: row[toIndex],
      });
    }

    onImportFn(mapping);
  }, [columns, fromColumn, isImportReady, onImportFn, rows, toColumn]);

  return (
    <ModalOverlay {...rest}>
      <Modal>
        <Dialog className="min-w-[24rem]">
          <div className="flex items-center border-b border-gray-200 p-4">
            {columns == null ? (
              <div className="item-center gap-xs flex flex-col">
                <Heading>Upload CSV</Heading>
                <Text>Select a CSV file to import from</Text>
              </div>
            ) : (
              <div className="item-center gap-xs flex flex-col">
                <Heading>Map CSV columns</Heading>
                <Text>Choose which columns will be used in mapping</Text>
              </div>
            )}

            <div className="grow" />

            <Button variant="plain" onPress={onClose}>
              <CloseIcon />
            </Button>
          </div>

          <div className="flex border-b border-gray-200 p-2">
            {columns == null ? (
              <DropZone
                className="h-[10rem] w-full"
                isDisabled={isUploading}
                onDropFiles={onDropFiles}
                acceptedMimeTypes={['text/csv']}
                maxFileSize={DOCUMENT_MAX_SIZE}
              >
                {isUploading ? <UploadProgress uploadProgress={uploadProgress} /> : <DropZoneButton />}
                {uploadError ? <Text variant="error">{uploadError}</Text> : null}
              </DropZone>
            ) : (
              <div className="gap-sm grid grid-cols-[auto_2rem_auto] items-center justify-center">
                <Text className="col-span-2">CSV column</Text>
                <Text>Map to</Text>

                <Select onChange={(value) => setFromColumn(value?.toString())}>
                  <Button variant="secondary">
                    <SelectValue />
                    <ChevronIcon rotation="down" />
                  </Button>

                  <Popover placement="left">
                    <ListBox>
                      {columns.map((column) => (
                        <ListBoxItem key={column} id={column}>
                          <Text>{column}</Text>
                        </ListBoxItem>
                      ))}
                    </ListBox>
                  </Popover>
                </Select>

                <div className="flex items-center justify-center">
                  <ArrowIcon className="justify-center" rotation="right" />
                </div>

                <div className="flex w-[6rem] items-center rounded-sm border border-gray-200 bg-gray-50 px-2 py-1">
                  <Text className="text-gray-500">From</Text>
                </div>

                <Select onChange={(value) => setToColumn(value?.toString())}>
                  <Button variant="secondary">
                    <SelectValue />
                    <ChevronIcon rotation="down" />
                  </Button>

                  <Popover placement="left">
                    <ListBox>
                      {columns.map((column) => (
                        <ListBoxItem key={column} id={column}>
                          <Text>{column}</Text>
                        </ListBoxItem>
                      ))}
                    </ListBox>
                  </Popover>
                </Select>

                <div className="flex items-center justify-center">
                  <ArrowIcon className="justify-center" rotation="right" />
                </div>

                <div className="flex w-[6rem] items-center rounded-sm border border-gray-200 bg-gray-50 px-2 py-1">
                  <Text className="text-gray-500">To</Text>
                </div>
              </div>
            )}
          </div>

          <div className="gap-sm flex items-center p-4">
            <div className="grow" />

            <Button variant="plain" onPress={onClose}>
              <Text>Cancel</Text>
            </Button>
            <Button isDisabled={isUploading || !isImportReady} variant="primary" onPress={onImport}>
              <Text>Save</Text>
            </Button>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};

export const ImportFromCSVDialog = memo(IImportFromCSVDialog) as typeof IImportFromCSVDialog;
